import { useEffect, useState } from "react";
import { PosTask, PreTask, SubTask, Task, TaskSession, aimsOfTask, proficiencyLevelsOfTask } from "../interfaces/Task";
import { Link, useParams } from "react-router-dom";
import PreviewHeader from "../header/PreviewHeader";
import { Button, ButtonGroup, Col, Container, Dropdown, DropdownButton, Form, Row } from "react-bootstrap";
import PreviewSubTask from "./SubTask/PreviewSubTask";
import PreviewPreTask from "./PreTaskTypes/PreviewPreTask";
import PreviewPosTask from "./PosTaskTypes/PreviewPosTask";
import Moment from 'moment';
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

export default function PreviewTask() {
    const { t } = useTranslation();
    
    const navigate = useNavigate();
    const params= useParams();
    const task_id: string = params.id === undefined ? '' : params.id;

    const [selectedSession, setSelectedSession] = useState('');
    const [isDownloadingTeacherDocx, setIsDownloadingTeacherDocx] = useState(false);
    const [isDownloadingTeacherHtml, setIsDownloadingTeacherHtml] = useState(false);
    const [isDownloadingTeacherScorm, setIsDownloadingTeacherScorm] = useState(false);
    const [isDownloadingStudentScorm, setIsDownloadingStudentScorm] = useState(false);
    const [isDownloadingStudentHtml, setIsDownloadingStudentHtml] = useState(false);
    const [isDownloadingStudentDocx, setIsDownloadingStudentDocx] = useState(false);
    const [task, setTask] = useState<Task>({
        _id: '',
        name: '',
        pretasks: [],
        tasks: [],
        postasks: [],
        updated: new Date(),
        userId: '',
        active: false,
        authorship: '',
        aims: [],
        aim_other: '',
        specific_aim: '',
        proficiency_level: [],
        proficiency_level_comments: '',
        sessions: [],
        clonned: false,
        original_source: '',
        original_task: '',
        weight: 0,    
        sectionId: '',
        task_language: 'en'
    });

    useEffect(() => {
        const requestOptionsPost = {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + localStorage.getItem('token')
            }
        };
        fetch(import.meta.env.VITE_API_URL + '/v1/task/' + params.id, requestOptionsPost)
            .then(response => {
                if (response.status !== 200) {
                    navigate('/');
                }
                return response.json();
            })
            .then(data => {
                if (data.aims === undefined) {
                    data.aims = [];
                }
                if (data.proficiency_level === undefined) {
                    data.proficiency_level = [];
                }
                setTask(data);
            });
    }, []);

    const handleNewSession = (event: any) => {
        event.preventDefault();
        const newSessions = task.sessions.map((s: TaskSession) => { return s; });
        const newSession: TaskSession = {
            _id: '',
            name: event.target.name.value,
            datetime: event.target.datetime.value
        }
        newSessions.push(newSession);
        const requestOptionsPost = {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + localStorage.getItem('token')
            },
            body: JSON.stringify(newSessions)
        };
        fetch(import.meta.env.VITE_API_URL + '/v1/task/sessions/' + task._id, requestOptionsPost)
            .then(response => {
                if (response.status !== 200) {
                    navigate('/');
                }
                return response.json();
            })
            .then(data => {
                setTask(data);
            });
    }

    const handleEditSession = (event: any) => {
        event.preventDefault();
        setSelectedSession(event.target.dataset.id);
    }  

    const handleDeleteSession = (event: any) => {
        event.preventDefault();
        const newSessions = task.sessions.filter((s: TaskSession) => event.target.dataset.id != s._id);
        const requestOptionsPost = {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + localStorage.getItem('token')
            },
            body: JSON.stringify(newSessions)
        };
        fetch(import.meta.env.VITE_API_URL + '/v1/task/sessions/' + task._id, requestOptionsPost)
            .then(response => {
                if (response.status !== 200) {
                    navigate('/');
                }
                return response.json();
            })
            .then(data => {
                setTask(data);
            });
    }

    const handleUpdateSession = (event: any) => {
        event.preventDefault();
        const newSessions = task.sessions.map((s: TaskSession) => {
            const curSession = Object.assign({}, s);
            if (s._id == selectedSession) {
                curSession.name = event.target.name.value;
                curSession.datetime = event.target.datetime.value;
            }
            return curSession;
        });
        const requestOptionsPost = {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + localStorage.getItem('token')
            },
            body: JSON.stringify(newSessions)
        };
        fetch(import.meta.env.VITE_API_URL + '/v1/task/sessions/' + task._id, requestOptionsPost)
            .then(response => {
                if (response.status !== 200) {
                    navigate('/');
                }
                return response.json();
            })
            .then(data => {
                setTask(data);
            });
        setSelectedSession('');
    }

    const cancelUpdateSession = (event: any) => {
        event.preventDefault();
        setSelectedSession('');
    }

    const downloadTeacherScorm = async () => {
        if (isDownloadingTeacherScorm) return;

        setIsDownloadingTeacherScorm(true);
        try {
            const response = await fetch(import.meta.env.VITE_API_URL + '/v1/task/' + params.id + '/export/scorm?type=T', {
                headers: {
                    'Authorization': 'Bearer ' + localStorage.getItem('token')
                }
            });

            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `task-${task.name || params.id}-teacher-scorm.zip`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
            } else {
                console.error('Failed to download teacher SCORM package');
            }
        } catch (error) {
            console.error('Error downloading teacher SCORM package:', error);
        } finally {
            setIsDownloadingTeacherScorm(false);
        }
    }

    const downloadTeacherHtml = async () => {
        if (isDownloadingTeacherHtml) return;

        setIsDownloadingTeacherHtml(true);
        try {
            const response = await fetch(import.meta.env.VITE_API_URL + '/v1/task/' + params.id + '/export/html?type=T', {
                headers: {
                    'Authorization': 'Bearer ' + localStorage.getItem('token')
                }
            });

            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `task-${task.name || params.id}-teacher.html`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
            } else {
                console.error('Failed to download teacher HTML file');
            }
        } catch (error) {
            console.error('Error downloading teacher HTML file:', error);
        } finally {
            setIsDownloadingTeacherHtml(false);
        }
    }

    const downloadTeacherDocx = async () => {
        if (isDownloadingTeacherDocx) return;

        setIsDownloadingTeacherDocx(true);
        try {
            const response = await fetch(import.meta.env.VITE_API_URL + '/v1/task/' + params.id + '/export/docx?type=T', {
                headers: {
                    'Authorization': 'Bearer ' + localStorage.getItem('token')
                }
            });

            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `task-${task.name || params.id}-teacher.docx`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
            } else {
                console.error('Failed to download teacher DOCX file');
            }
        } catch (error) {
            console.error('Error downloading teacher DOCX file:', error);
        } finally {
            setIsDownloadingTeacherDocx(false);
        }
    }

    const handleExportTeachersVersion = (event: any) => {
        event.preventDefault();
        const requestOptionsPost = {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + localStorage.getItem('token')
            }
        };
        fetch(import.meta.env.VITE_API_URL + '/v1/task/' + task._id + '/export/t', requestOptionsPost)
            .then(response => {
                if (response.status !== 200) {
                    navigate('/');
                }
                return response.json();
            })
            .then(data => {
                window.open(data.zip, '_blank');
            });
    }

    const downloadStudentScorm = async () => {
        if (isDownloadingStudentScorm) return;

        setIsDownloadingStudentScorm(true);
        try {
            const response = await fetch(import.meta.env.VITE_API_URL + '/v1/task/' + params.id + '/export/scorm?type=S', {
                headers: {
                    'Authorization': 'Bearer ' + localStorage.getItem('token')
                }
            });

            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `task-${task.name || params.id}-student-scorm.zip`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
            } else {
                console.error('Failed to download student SCORM package');
            }
        } catch (error) {
            console.error('Error downloading student SCORM package:', error);
        } finally {
            setIsDownloadingStudentScorm(false);
        }
    }


    const downloadStudentHtml = async () => {
        if (isDownloadingStudentHtml) return;

        setIsDownloadingStudentHtml(true);
        try {
            const response = await fetch(import.meta.env.VITE_API_URL + '/v1/task/' + params.id + '/export/html?type=S', {
                headers: {
                    'Authorization': 'Bearer ' + localStorage.getItem('token')
                }
            });

            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `task-${task.name || params.id}-student.html`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
            } else {
                console.error('Failed to download student HTML file');
            }
        } catch (error) {
            console.error('Error downloading student HTML file:', error);
        } finally {
            setIsDownloadingStudentHtml(false);
        }
    }

    const downloadStudentDocx = async () => {
        if (isDownloadingStudentDocx) return;

        setIsDownloadingStudentDocx(true);
        try {
            const response = await fetch(import.meta.env.VITE_API_URL + '/v1/task/' + params.id + '/export/docx?type=S', {
                headers: {
                    'Authorization': 'Bearer ' + localStorage.getItem('token')
                }
            });

            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `task-${task.name || params.id}-student.docx`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
            } else {
                console.error('Failed to download student DOCX file');
            }
        } catch (error) {
            console.error('Error downloading student DOCX file:', error);
        } finally {
            setIsDownloadingStudentDocx(false);
        }
    }

    const openTeachersSite = () => {
        window.open('/#/task-site/' + params.id + '/t', '_blank');
        // navigate('/task-site/' + params.id + '/t');
    }

    const openStudentsSite = () => {
        window.open('/#/task-site/' + params.id + '/s', '_blank');
        // navigate('/task-site/' + params.id + '/s');
    }


    return (
        <div className="page">
            <div className="preview-task">
                <PreviewHeader />
                <Container>
                    <Row>
                        <Col>
                            <Link className="back" to={ '/edit-task/' + task_id }>{ 'back to the task' }</Link>
                            <h1 className="task-title">
                                { t("My design") }
                                <div className="export-actions">
                                    <ButtonGroup className="teacher-actions">
                                        <DropdownButton as={ ButtonGroup } title={
                                            <>
                                                <span className="teacher-icon"></span>
                                                { t("Teacher version") }
                                            </>
                                        }>
                                            <Dropdown.Item eventKey="1" onClick={ openTeachersSite }>
                                                <span className="preview-icon"></span>
                                                { t("Preview") }
                                            </Dropdown.Item>
                                            <Dropdown.Item eventKey="2" onClick={ downloadTeacherScorm } disabled={ isDownloadingTeacherScorm }>
                                                {isDownloadingTeacherScorm ? (
                                                    <>
                                                        <span className="spinner-small"></span>
                                                        { t("Downloading...") }
                                                    </>
                                                ) : (
                                                    <>
                                                        <span className="scorm-icon-small"></span>
                                                        { t("Download SCORM") }
                                                    </>
                                                )}
                                            </Dropdown.Item>
                                            <Dropdown.Item eventKey="3" onClick={ downloadTeacherHtml } disabled={ isDownloadingTeacherHtml }>
                                                {isDownloadingTeacherHtml ? (
                                                    <>
                                                        <span className="spinner-small"></span>
                                                        { t("Downloading...") }
                                                    </>
                                                ) : (
                                                    <>
                                                        <span className="html-icon-small"></span>
                                                        { t("Download HTML") }
                                                    </>
                                                )}
                                            </Dropdown.Item>
                                            <Dropdown.Item eventKey="4" onClick={ downloadTeacherDocx } disabled={ isDownloadingTeacherDocx }>
                                                {isDownloadingTeacherDocx ? (
                                                    <>
                                                        <span className="spinner-small"></span>
                                                        { t("Downloading...") }
                                                    </>
                                                ) : (
                                                    <>
                                                        <span className="word-icon-small"></span>
                                                        { t("Download DOCX") }
                                                    </>
                                                )}
                                            </Dropdown.Item>
                                        </DropdownButton>
                                    </ButtonGroup>
                                    {/* <Help id={ '654367608fb78d09dce65abe' } /> */}

                                    <ButtonGroup className="student-actions">
                                        <DropdownButton as={ ButtonGroup } title={
                                            <>
                                                <span className="student-icon"></span>
                                                { t("Student version") }
                                            </>
                                        }>
                                            <Dropdown.Item eventKey="1" onClick={ openStudentsSite }>
                                                <span className="preview-icon"></span>
                                                { t("Preview") }
                                            </Dropdown.Item>
                                            <Dropdown.Item eventKey="2" onClick={ downloadStudentScorm } disabled={ isDownloadingStudentScorm }>
                                                {isDownloadingStudentScorm ? (
                                                    <>
                                                        <span className="spinner-small"></span>
                                                        { t("Downloading...") }
                                                    </>
                                                ) : (
                                                    <>
                                                        <span className="scorm-icon-small"></span>
                                                        { t("Download SCORM") }
                                                    </>
                                                )}
                                            </Dropdown.Item>
                                            <Dropdown.Item eventKey="3" onClick={ downloadStudentHtml } disabled={ isDownloadingStudentHtml }>
                                                {isDownloadingStudentHtml ? (
                                                    <>
                                                        <span className="spinner-small"></span>
                                                        { t("Downloading...") }
                                                    </>
                                                ) : (
                                                    <>
                                                        <span className="html-icon-small"></span>
                                                        { t("Download HTML") }
                                                    </>
                                                )}
                                            </Dropdown.Item>
                                            <Dropdown.Item eventKey="4" onClick={ downloadStudentDocx } disabled={ isDownloadingStudentDocx }>
                                                {isDownloadingStudentDocx ? (
                                                    <>
                                                        <span className="spinner-small"></span>
                                                        { t("Downloading...") }
                                                    </>
                                                ) : (
                                                    <>
                                                        <span className="word-icon-small"></span>
                                                        { t("Download DOCX") }
                                                    </>
                                                )}
                                            </Dropdown.Item>
                                        </DropdownButton>
                                    </ButtonGroup>

                                </div>
                            </h1>
                            <div className="task-header">
                                <div className="inner">
                                    <div className="title">
                                        <span className="label">{ t("Title") }:</span> { task.name }
                                    </div>
                                    <div className="authorship">
                                        <span className="label">{ t("Authorship") }:</span> { task.authorship }
                                    </div>
                                    <div className="proficiency-level">
                                        <span className="label">{ t("Student's proficiency level") }:</span>
                                        { proficiencyLevelsOfTask.map((l: any) => {
                                            const index: number = task.proficiency_level.indexOf(l.value);
                                            if (index !== -1) {
                                                return (<span className="value">{ t(l.description) }</span>);
                                            }
                                        })}
                                    </div>
                                    <div className="aims">
                                        <span className="label">{ t("Aims") }:</span>
                                        { aimsOfTask.map((a: any) => {
                                            const index: number = task.aims.indexOf(a.value);
                                            if (index !== -1) {
                                                return (<span className="value">{ t(a.description) }</span>);
                                            }
                                        })}
                                        { task.specific_aim &&
                                            <span className="value">{ task.specific_aim }</span>
                                        }
                                    </div>
                                </div>
                            </div>
                            <div className="task-sessions">
                                <h4>{ t("Sessions") }</h4>
                                <div className="sessions-list">
                                    { task.sessions.length > 0 &&
                                        <ul>
                                            { task.sessions.sort((a: TaskSession, b: TaskSession) => parseInt(Moment(a.datetime).format('YYYYMMDD')) - parseInt(Moment(b.datetime).format('YYYYMMDD')))
                                                .map((s: TaskSession) => {
                                                    if (s._id == selectedSession) {
                                                        return <Form className="edit-session-form" onSubmit={ handleUpdateSession }>
                                                            <Form.Control type="hidden" name="id" value={ s._id} />
                                                            <Form.Group className="field field-name">
                                                                <Form.Control type="text" name="name" placeholder={ t("Session name") }
                                                                    required defaultValue={ s.name } />
                                                            </Form.Group>
                                                            <Form.Group className="field field-datetime">
                                                                <Form.Control type="date" name="datetime" required defaultValue={ Moment(s.datetime).format('YYYY-MM-DD') } />
                                                            </Form.Group>
                                                            <Form.Group className="actions">
                                                                <Button className="btn btn-primary" type="submit">
                                                                    { t("Update") }
                                                                </Button>
                                                                <Button className="btn btn-secondary" onClick={ cancelUpdateSession }>
                                                                    { t("Cancel") }
                                                                </Button>
                                                            </Form.Group>
                                                        </Form>;
                                                    } else {
                                                    return <li key={ 'session-' + s._id }>
                                                            <div className="name">{ s.name }</div>
                                                            <div className="date">({ Moment(s.datetime).format('DD/MM/YYYY') })</div>
                                                            <div className="actions">
                                                                <a className="edit-link" data-id={ s._id } onClick={ handleEditSession }></a>
                                                                <a className="delete-link" data-id={ s._id } onClick={ handleDeleteSession }></a>
                                                            </div>
                                                        </li>;
                                                    }
                                                })
                                            }
                                        </ul>
                                    }
                                    { selectedSession == '' &&
                                        <Form className="new-session-form" onSubmit={ handleNewSession }>
                                            <Form.Group className="field field-name">
                                                <Form.Control type="text" name="name" placeholder={ t("Session name") } required />
                                            </Form.Group>
                                            <Form.Group className="field field-datetime">
                                                <Form.Control type="date" name="datetime" required />
                                            </Form.Group>
                                            <Form.Group className="actions">
                                                <Button className="btn btn-primary" type="submit">
                                                    { t("Add session") }
                                                </Button>
                                            </Form.Group>
                                        </Form>
                                    }   
                                </div>
                            </div>
                            <div className="pretasks">
                                <h2>{ t("Pre-Task") }</h2>
                                { task.pretasks.length == 0 &&
                                    <div className="message error">
                                        { t("There are no pre-tasks defined") }
                                    </div>
                                }
                                { task_id !== undefined && task.pretasks.length > 0 &&
                                    <div className="pretasks-list">
                                        { task.pretasks.map((t: PreTask) => {
                                            return (<PreviewPreTask task_id={ task_id }
                                                        pretask={ t } key={ 'pretask-' + t._id }
                                                        sessions={ task.sessions } />)
                                        })}
                                    </div>
                                }
                            </div>
                            <div className="tasks">
                                <h2>{ t("Main Tasks") }</h2>
                                { task.tasks.length == 0 && 
                                    <div className="message error">
                                        { t("There are no tasks defined") }
                                    </div>
                                }
                                { task_id !== undefined && task.tasks.length > 0 &&
                                    <div className="tasks-list">
                                        { task.tasks.map((t: SubTask) => {
                                            return (<PreviewSubTask task_id={ task_id } 
                                                subtask={ t } key={ 'subtask-' + t._id }
                                                sessions={ task.sessions } />)
                                        })}
                                    </div>
                                }
                            </div>
                            <div className="postasks">
                                <h2>{ t("Post-Task") }</h2>
                                { task.postasks.length == 0 && 
                                    <div className="message error">
                                        { t("There are no post-tasks defined") }
                                    </div>
                                }
                                { task_id !== undefined && task.pretasks.length > 0 &&
                                    <div className="postasks-list">
                                        { task.postasks.map((t: PosTask) => {
                                            return (<PreviewPosTask task_id={ task_id } 
                                                postask={ t } key={ 'postask-' + t._id } sessions={ task.sessions } />)
                                        })}
                                    </div>
                                }
                            </div>
                        </Col>
                    </Row>
                </Container>
            </div>
        </div>
    );
}